import * as React from "react";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import Container from "@amzn/awsui-components-react/polaris/container";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Select from "@amzn/awsui-components-react/polaris/select";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Toggle from "@amzn/awsui-components-react/polaris/toggle";
import { API, Auth } from "aws-amplify";
import {
    ShipmentOperation,
    OPERATION_OPTIONS,
    executeShipmentWorkflowDefault,
} from "./request/ExecuteShipmentWorkflowRequest";

interface IState {
    operation: { label: string; value: string } | null;
    orderId: string;
    shipmentId: string;
    realm: string;
    isJsonInput: boolean;
    jsonInput: string;
    isLoading: boolean;
    flashItems: any[];
    response: any;
}

class ExecuteShipmentWorkflowSection extends React.Component<{}, IState> {
    constructor(props: {}) {
        super(props);
        this.state = {
            operation: null,
            orderId: "",
            shipmentId: "",
            realm: "USAmazon",
            isJsonInput: false,
            jsonInput: executeShipmentWorkflowDefault,
            isLoading: false,
            flashItems: [],
            response: null,
        };
    }

    private get needsOrderId(): boolean {
        // All operations require orderId
        return this.state.operation != null;
    }

    private get needsShipmentId(): boolean {
        // Only fakeShipComplete requires shipmentId (in addition to orderId)
        return this.state.operation?.value === ShipmentOperation.FAKE_SHIP_COMPLETE;
    }

    render() {
        return (
            <SpaceBetween size="l">
                <Container
                    header={
                        <Header
                            variant="h2"
                            description="Execute shipment workflow operations against the ApaqPortalService"
                        >
                            Execute Shipment Workflow
                        </Header>
                    }
                >
                    <SpaceBetween size="l">
                        <Toggle
                            checked={this.state.isJsonInput}
                            onChange={({ detail }) => this.setState({ isJsonInput: detail.checked })}
                        >
                            JSON Input Mode
                        </Toggle>

                        {!this.state.isJsonInput ? (
                            <SpaceBetween size="m">
                                <FormField label="Operation" description="Select the shipment operation to execute">
                                    <Select
                                        selectedOption={this.state.operation}
                                        onChange={({ detail }) =>
                                            this.setState({
                                                operation: detail.selectedOption as any,
                                                shipmentId: "",
                                            })
                                        }
                                        options={OPERATION_OPTIONS}
                                        placeholder="Select an operation"
                                    />
                                </FormField>

                                {this.needsOrderId && (
                                    <FormField label="Order ID" description="The order ID to process">
                                        <Input
                                            value={this.state.orderId}
                                            onChange={({ detail }) => this.setState({ orderId: detail.value })}
                                            placeholder="e.g. 903-3028971-5297443"
                                        />
                                    </FormField>
                                )}

                                {this.needsShipmentId && (
                                    <FormField label="Shipment ID" description="The shipment ID to complete">
                                        <Input
                                            value={this.state.shipmentId}
                                            onChange={({ detail }) => this.setState({ shipmentId: detail.value })}
                                            placeholder="e.g. 10013151971390"
                                        />
                                    </FormField>
                                )}

                                <FormField label="Realm" description="Optional, defaults to USAmazon">
                                    <Input
                                        value={this.state.realm}
                                        onChange={({ detail }) => this.setState({ realm: detail.value })}
                                    />
                                </FormField>
                            </SpaceBetween>
                        ) : (
                            <FormField label="JSON Request Body">
                                <Textarea
                                    value={this.state.jsonInput}
                                    onChange={({ detail }) => this.setState({ jsonInput: detail.value })}
                                    rows={10}
                                />
                            </FormField>
                        )}

                        <Button
                            variant="primary"
                            loading={this.state.isLoading}
                            onClick={() => this.handleSubmit()}
                        >
                            Submit
                        </Button>
                    </SpaceBetween>
                </Container>

                {this.state.flashItems.length > 0 && (
                    <Flashbar items={this.state.flashItems.map(item => ({
                        ...item,
                        onDismiss: () => this.setState({ flashItems: [] }),
                    }))} />
                )}

                {this.state.isLoading && (
                    <Box textAlign="center" padding="xxl">
                        <Spinner size="large" />
                    </Box>
                )}

                {this.state.response && (
                    <Container header={<Header variant="h2">Response</Header>}>
                        <Box variant="code">
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
                                {typeof this.state.response === "string"
                                    ? this.state.response
                                    : JSON.stringify(this.state.response, null, 2)}
                            </pre>
                        </Box>
                    </Container>
                )}
            </SpaceBetween>
        );
    }

    private handleSubmit() {
        this.setState({ isLoading: true, flashItems: [], response: null });

        let body: any;
        if (this.state.isJsonInput) {
            try {
                body = JSON.parse(this.state.jsonInput);
            } catch (e) {
                this.setState({
                    isLoading: false,
                    flashItems: [{ type: "error", content: `Invalid JSON: ${e instanceof Error ? e.message : String(e)}`, dismissible: true }],
                });
                return;
            }
        } else {
            const errors: string[] = [];
            if (!this.state.operation) errors.push("operation");
            if (!this.state.orderId) errors.push("order ID");
            if (this.needsShipmentId && !this.state.shipmentId) errors.push("shipment ID");

            if (errors.length > 0) {
                this.setState({
                    isLoading: false,
                    flashItems: [{ type: "error", content: `Please provide: ${errors.join(", ")}`, dismissible: true }],
                });
                return;
            }

            body = {
                operation: this.state.operation!.value,
                orderId: this.state.orderId,
                ...(this.needsShipmentId && { shipmentId: this.state.shipmentId }),
                ...(this.state.realm && { realm: this.state.realm }),
            };
        }

        this.callExecuteShipmentWorkflow(body);
    }

    private async callExecuteShipmentWorkflow(body: any) {
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await API.post("ApaqPortalServiceAPI", "/", {
                body,
                headers: {
                    "Content-Type": "application/json",
                    "Content-Encoding": "amz-1.0",
                    "X-Amz-Target": "com.amazon.apaqportalservice.ApaqPortalService.ExecuteShipmentWorkflow",
                    "Authorization": `Bearer ${token}`,
                },
            });

            this.setState({
                response,
                isLoading: false,
                flashItems: [{ type: "success", content: "Shipment workflow executed successfully", dismissible: true }],
            });
        } catch (error) {
            const err = error as any;
            this.setState({
                response: null,
                isLoading: false,
                flashItems: [{
                    type: "error",
                    content: err.response?.data?.message || err.message || "An unknown error occurred",
                    dismissible: true,
                }],
            });
        }
    }
}

export default ExecuteShipmentWorkflowSection;
